import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const NAV = [
  { label: "Boutique", to: "/boutique" },
  { label: "Suspensions", to: "/categorie/suspensions" },
  { label: "Plafonniers", to: "/categorie/plafonniers" },
  { label: "Lampadaires", to: "/categorie/lampadaires" },
  { label: "Appliques", to: "/categorie/appliques" },
];

export const Header = ({ onSearch }) => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
    <header
      data-testid="site-header"
      className="fixed top-9 z-40 w-full backdrop-blur-xl bg-cream/85 border-b border-stone/60"
    >
      <div className="flex items-center justify-between h-16 md:h-20 px-5 md:px-10">
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-ink relative z-10"
          onClick={() => setMenuOpen(true)}
          aria-label="Menu"
        >
          <Menu className="h-6 w-6" strokeWidth={1.5} />
        </button>

        <Link
          to="/"
          data-testid="logo-link"
          aria-label="Déstockage Luminaires — accueil"
          className="select-none absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0 inline-flex items-center hover:opacity-90 transition-opacity"
        >
          <Logo to={null} testId="logo-inner" size="md" variant="dark" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-xs font-sans uppercase tracking-[0.18em] text-graphite hover:text-ink transition-colors"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4 md:gap-5 relative z-10">
          <button
            data-testid="open-search-btn"
            onClick={onSearch}
            className="text-ink hover:text-gold transition-colors"
            aria-label="Rechercher"
          >
            <Search className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <button
            data-testid="account-link"
            onClick={() => navigate(user ? (user.role === "admin" ? "/admin" : "/compte") : "/connexion")}
            className="hidden md:block text-ink hover:text-gold transition-colors"
            aria-label="Compte"
          >
            <User className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <button
            data-testid="open-cart-btn"
            onClick={() => setOpen(true)}
            className="relative text-ink hover:text-gold transition-colors"
            aria-label="Panier"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-ink text-cream text-[10px] font-mono h-4 w-4 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>

    {/* Mobile drawer menu — RENDU HORS <header> car backdrop-blur crée un containing block
        qui casserait le `position: fixed` du drawer (drawer collapse à la hauteur du header). */}
    {menuOpen && (
      <div className="fixed inset-0 z-[60] md:hidden">
        <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
        <div className="absolute left-0 top-0 h-full w-[82%] max-w-xs bg-cream p-6 animate-fade-in flex flex-col shadow-2xl">
          <div className="flex items-center justify-between mb-8">
            <Logo to={null} size="sm" variant="dark" testId="mobile-menu-logo" />
            <button
              data-testid="close-mobile-menu"
              onClick={() => setMenuOpen(false)}
              aria-label="Fermer le menu"
              className="text-ink p-1 -mr-1"
            >
              <X className="h-6 w-6" strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex flex-col gap-5 flex-1 overflow-y-auto">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMenuOpen(false)}
                data-testid={`drawer-nav-${n.label.toLowerCase()}`}
                className="font-serif text-2xl text-ink hover:text-gold transition-colors"
              >
                {n.label}
              </Link>
            ))}
            <div className="h-px bg-stone/60 my-2" />
            <Link
              to={user ? (user.role === "admin" ? "/admin" : "/compte") : "/connexion"}
              onClick={() => setMenuOpen(false)}
              data-testid="drawer-account-link"
              className="font-serif text-2xl text-ink hover:text-gold transition-colors"
            >
              {user ? (user.role === "admin" ? "Administration" : "Mon compte") : "Se connecter"}
            </Link>
          </nav>
        </div>
      </div>
    )}
    </>
  );
};
